import React, { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCountdown } from "@/hooks/useCountdown";

const WARNING_THRESHOLD_MS = 2 * 60 * 1000;
const URGENT_THRESHOLD_MS = 30 * 1000;

const HOLD_EXPLANATION =
  "We're holding this appointment slot for you. If time runs out the slot is released and you'll need to start again.";

// Coarse announcement points, in descending order. A screen reader reading every tick of a
// `role="timer"` would be unusable, so the live region below speaks only as each of these is
// crossed — enough warning to act on, quiet the rest of the time.
const ANNOUNCE_AT_MS = [2 * 60 * 1000, 60 * 1000, 30 * 1000] as const;

function announcementFor(msRemaining: number): string | null {
  const threshold = ANNOUNCE_AT_MS.find(t => msRemaining <= t);
  if (threshold === undefined) {
    return null;
  }

  const label = threshold >= 60_000 ? `${threshold / 60_000} minute${threshold === 60_000 ? "" : "s"}` : "30 seconds";
  return `${label} left to finish before your appointment slot is released.`;
}

export const ReservationTimer = ({ msRemaining }: { msRemaining: number }) => {
  const [announcement, setAnnouncement] = useState("");
  const announcedThresholdRef = useRef<number | null>(null);

  useEffect(() => {
    const threshold = ANNOUNCE_AT_MS.find(t => msRemaining <= t) ?? null;
    if (threshold === null || threshold === announcedThresholdRef.current) {
      return;
    }
    announcedThresholdRef.current = threshold;
    setAnnouncement(announcementFor(msRemaining) ?? "");
  }, [msRemaining]);

  const urgent = msRemaining <= URGENT_THRESHOLD_MS;
  const warning = !urgent && msRemaining <= WARNING_THRESHOLD_MS;

  return (
    <div className="w-full max-w-xl mx-auto mb-3 flex justify-end">
      <span
        role="timer"
        title={HOLD_EXPLANATION}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium tabular-nums transition-colors",
          !warning && !urgent && "bg-slate-100 border-slate-200 text-slate-600",
          warning && "bg-amber-50 border-amber-200 text-amber-800",
          urgent && "bg-red-50 border-red-200 text-red-700",
        )}
      >
        <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
        {formatCountdown(msRemaining)}
        <span className="sr-only">remaining to hold your appointment slot</span>
      </span>

      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
};
